import Loader from '@/components/Loader/Loader';
import StatusBadge from '@/components/shared/Badge/Badge';
import Divider from '@/components/shared/Divider/Divider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import LayoutInside from '@/layout/LayoutInside';
import SaleSlider from '@/components/sales/SaleSlider';
import ClientCircle from '@/assets/icons/ClientCircle';
import Conversion from '@/assets/icons/Conversion';
import Details from '@/assets/icons/Details';
import TagsCircle from '@/assets/icons/TagsCircle';
import WhatsIcon from '@/assets/icons/WhatsIcon';
import { getIconMethodPayment } from '@/utils/getIconMethodPayment';
import { resolveSubscriptionDisplayStatus } from '@/utils/resolveSubscriptionDisplayStatus';
import { countries } from 'country-data-list';
import { ArrowLeft, ArrowRight, ExternalLink, Info, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Sticky, StickyContainer } from 'react-sticky';
import { toast } from '@/hooks/use-toast';
import CancelSubscriptionDialog from './Partials/CancelSubscriptionDialog';
import SubscriptionBillingSection from './Partials/SubscriptionBillingSection';
import { useSubscriptionDetails } from './hooks/useSubscriptionDetails';

export default function SubscriptionDetails() {
  const { t } = useTranslation(['signatures', 'sales', 'common']);
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);

  const {
    detail,
    isLoading,
    isError,
    cancelSubscription,
    isCanceling,
    refetch,
  } = useSubscriptionDetails(publicId);

  const subscription = detail?.subscription;
  const signupOrder = detail?.signup_order;
  const actions = detail?.actions;
  const displayStatus = subscription
    ? resolveSubscriptionDisplayStatus(subscription, (key) =>
        t(`signatures:status.${key}`, { defaultValue: key }),
      )
    : null;
  const cancelRequested = Boolean(
    subscription?.cancel_requested_at
    || subscription?.cancel_requested_at_formatted,
  );
  const accessUntilDate = subscription?.ends_at_formatted
    ?? subscription?.next_billing_at_formatted
    ?? null;

  const breadcrumbItems = [
    { text: t('signatures:title'), link: '/signatures' },
    {
      text: t('details.title'),
      link: `/signatures/subscriptions-details/${publicId}`,
    },
  ];

  const handleWhatsApp = () => {
    const phone = signupOrder?.customer?.phone;
    const prefix = signupOrder?.customer?.phone_prefix ?? '55';
    if (!phone) return;
    window.open(`https://wa.me/${prefix}${phone}`, '_blank');
  };

  const handleCancel = async () => {
    try {
      await cancelSubscription();
      setCancelOpen(false);
      toast({ title: t('details.cancel_success') });
      await refetch();
    } catch {
      toast({
        title: t('details.cancel_error'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <LayoutInside items={breadcrumbItems}>
        <Loader fullScreen />
      </LayoutInside>
    );
  }

  if (isError || !subscription) {
    return (
      <LayoutInside items={breadcrumbItems}>
        <Alert variant="destructive">
          <AlertDescription>{t('details.load_error')}</AlertDescription>
        </Alert>
      </LayoutInside>
    );
  }

  const saleSliderData = signupOrder?.offers?.length
    ? ({
        offers: signupOrder.offers,
        currency: signupOrder.currency,
        settlement_currency: signupOrder.settlement_currency,
        currencyConversion: signupOrder.currencyConversion,
      } as any)
    : null;

  const currencyConversion = signupOrder?.currencyConversion ?? null;
  const settlementCurrency =
    currencyConversion?.to_currency ||
    signupOrder?.settlement_currency ||
    signupOrder?.currency ||
    null;
  const catalogCurrency = subscription.currency;
  const showCatalogCurrencyNote = Boolean(
    settlementCurrency &&
      catalogCurrency &&
      settlementCurrency.toUpperCase() !== catalogCurrency.toUpperCase(),
  );
  const catalogCurrencyNote = showCatalogCurrencyNote
    ? t('details.catalog_currency_note', {
        currency: catalogCurrency,
        amount: subscription.amount_formatted,
        to_currency: settlementCurrency,
      })
    : null;
  const shippingEstimate = subscription.next_shipping_estimate_formatted
    ? t('details.shipping_estimate', {
        amount: subscription.next_shipping_estimate_formatted,
      })
    : null;

  const allCountries = countries.all;
  const customer = signupOrder?.customer;
  const countryByPhonePrefix = allCountries.find((item) =>
    item.countryCallingCodes.includes(`${customer?.phone_prefix}`),
  );
  const countryByCode = customer?.country?.code
    ? allCountries.find(
        (item) =>
          item.alpha2 === customer.country?.code ||
          item.alpha3 === customer.country?.code,
      )
    : undefined;
  const countryEmoji = countryByCode?.emoji || countryByPhonePrefix?.emoji || '🇧🇷';
  const countryName = countryByCode?.name || countryByPhonePrefix?.name || 'Brasil';
  const countryLabel = `${countryEmoji} ${countryName}`;

  return (
    <LayoutInside items={breadcrumbItems}>
      <Button
        variant="default"
        className="block xl:hidden bg-transparent text-colorText hover:bg-transparent"
        onClick={() => navigate('/signatures')}
      >
        <ArrowLeft size={16} />
        {t('details.back')}
      </Button>

      <div className="flex gap-4 justify-between w-full max-xl:flex-col">
        <StickyContainer>
          <Sticky>
            {({ style, isSticky }) => (
              <section
                style={style}
                className={`w-[328px] ${isSticky ? 'mt-6' : ''} hidden xl:block`}
              >
                <Button
                  variant="default"
                  className="bg-transparent text-colorText hover:bg-transparent"
                  onClick={() => navigate('/signatures')}
                >
                  <ArrowLeft size={16} />
                  {t('details.back')}
                </Button>

                <Card className="lg:w-[328px] w-full p-4 md:p-6 mt-6">
                  <p className="text-muted text-sm">{t('details.total_amount')}</p>
                  <h2 className="text-muted flex items-center gap-1.5">
                    <strong className="text-colorText">
                      {subscription.amount_formatted}
                    </strong>
                    {catalogCurrencyNote && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex text-muted hover:text-colorText"
                              aria-label={catalogCurrencyNote}
                            >
                              <Info size={14} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-[260px] text-xs leading-relaxed"
                          >
                            {catalogCurrencyNote}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </h2>
                  {shippingEstimate && (
                    <p className="text-muted text-xs mt-1">{shippingEstimate}</p>
                  )}

                  <Divider className="my-4" />

                  <article className="space-y-6 text-sm mt-4">
                    <article>
                      <p className="text-muted text-sm">{t('details.status')}</p>
                      {displayStatus && (
                        <StatusBadge
                          status={displayStatus.status}
                          text={displayStatus.text}
                        />
                      )}
                    </article>

                    {cancelRequested && subscription.cancel_requested_at_formatted && (
                      <article>
                        <p className="text-muted text-sm">
                          {t('details.cancel_requested_at')}
                        </p>
                        <span className="text-colorText text-sm">
                          {subscription.cancel_requested_at_formatted}
                        </span>
                      </article>
                    )}

                    {cancelRequested && accessUntilDate && (
                      <article>
                        <p className="text-muted text-sm">{t('details.access_until')}</p>
                        <span className="text-colorText text-sm">{accessUntilDate}</span>
                      </article>
                    )}

                    {subscription.payment_method?.type && (
                      <article>
                        <p className="text-muted text-sm">{t('details.payment_method')}</p>
                        <div className="flex items-center gap-2">
                          {getIconMethodPayment(
                            subscription.payment_method.type,
                            subscription.payment_method.brand,
                          )}
                          <span>{t(`common:${subscription.payment_method.type}`)}</span>
                        </div>
                      </article>
                    )}

                    {subscription.in_trial && subscription.trial_ends_at_formatted && (
                      <article>
                        <p className="text-muted text-sm">{t('details.in_trial', { date: subscription.trial_ends_at_formatted })}</p>
                      </article>
                    )}

                    {!cancelRequested && (
                      <article>
                        <p className="text-muted text-sm">{t('details.next_billing')}</p>
                        <span className="text-colorText text-sm">
                          {subscription.next_billing_at_formatted ?? '—'}
                        </span>
                      </article>
                    )}

                    {subscription.ends_at_formatted && (
                      <article>
                        <p className="text-muted text-sm">{t('details.ends_at')}</p>
                        <span className="text-colorText text-sm">
                          {subscription.ends_at_formatted}
                        </span>
                      </article>
                    )}
                  </article>

                  {actions?.can_cancel && (
                    <Button
                      variant="secondary"
                      className="mt-6 w-full"
                      onClick={() => setCancelOpen(true)}
                    >
                      <XCircle size={16} />
                      {t('details.cancel_subscription')}
                    </Button>
                  )}
                </Card>
              </section>
            )}
          </Sticky>
        </StickyContainer>

        <section className="w-full xl:max-w-[594px]">
          <section className="mt-6">
            <div className="flex items-center gap-1">
              <Details />
              <h2 className="text-colorText font-semibold">{t('details.title')}</h2>
            </div>

            <div className="w-full mt-4 xl:hidden">
              <p className="text-muted text-sm">{t('details.total_amount')}</p>
              <h2 className="text-muted flex items-center gap-1.5">
                <strong className="text-colorText">
                  {subscription.amount_formatted}
                </strong>
                {catalogCurrencyNote && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex text-muted hover:text-colorText"
                          aria-label={catalogCurrencyNote}
                        >
                          <Info size={14} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[260px] text-xs leading-relaxed"
                      >
                        {catalogCurrencyNote}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </h2>
              {shippingEstimate && (
                <p className="text-muted text-xs mt-1">{shippingEstimate}</p>
              )}

              {actions?.can_cancel && (
                <Button
                  variant="secondary"
                  className="mt-6 max-xl:w-full"
                  onClick={() => setCancelOpen(true)}
                >
                  <XCircle size={16} />
                  {t('details.cancel_subscription')}
                </Button>
              )}
            </div>

            <section className="space-y-6 grid grid-cols-1 lg:grid-cols-2 text-sm">
              <article className="xl:hidden mt-4">
                <p className="text-muted text-sm">{t('details.status')}</p>
                {displayStatus && (
                  <StatusBadge
                    status={displayStatus.status}
                    text={displayStatus.text}
                  />
                )}
              </article>

              {cancelRequested && subscription.cancel_requested_at_formatted && (
                <article className="xl:hidden">
                  <p className="text-muted text-sm">{t('details.cancel_requested_at')}</p>
                  <span className="text-colorText text-sm">
                    {subscription.cancel_requested_at_formatted}
                  </span>
                </article>
              )}

              {cancelRequested && accessUntilDate && (
                <article className="xl:hidden">
                  <p className="text-muted text-sm">{t('details.access_until')}</p>
                  <span className="text-colorText text-sm">{accessUntilDate}</span>
                </article>
              )}

              {subscription.payment_method?.type && (
                <article className="xl:hidden">
                  <p className="text-muted text-sm">{t('details.payment_method')}</p>
                  <div className="flex items-center gap-2">
                    {getIconMethodPayment(
                      subscription.payment_method.type,
                      subscription.payment_method.brand,
                    )}
                    <span>{t(`common:${subscription.payment_method.type}`)}</span>
                  </div>
                </article>
              )}

              {!cancelRequested && (
                <article className="xl:hidden">
                  <p className="text-muted text-sm">{t('details.next_billing')}</p>
                  <span className="text-colorText text-sm">
                    {subscription.next_billing_at_formatted ?? '—'}
                  </span>
                </article>
              )}

              {subscription.ends_at_formatted && (
                <article className="xl:hidden">
                  <p className="text-muted text-sm">{t('details.ends_at')}</p>
                  <span className="text-colorText text-sm">
                    {subscription.ends_at_formatted}
                  </span>
                </article>
              )}

              <article>
                <p className="text-muted text-sm">{t('details.subscription_id')}</p>
                <span className="text-colorText text-sm break-all">
                  {subscription.public_id}
                </span>
              </article>

              <article>
                <p className="text-muted text-sm">{t('details.interval')}</p>
                <span className="text-colorText text-sm">
                  {subscription.interval_label_key
                    ? t(`common:paymentFrequency.${subscription.interval_label_key}`)
                    : '—'}
                </span>
              </article>

              <article>
                <p className="text-muted text-sm">{t('details.current_cycle')}</p>
                <span className="text-colorText text-sm">
                  {subscription.current_cycle ?? '—'}
                  {subscription.max_cycles ? ` / ${subscription.max_cycles}` : ''}
                </span>
              </article>

              <article>
                <p className="text-muted text-sm">{t('details.started_at')}</p>
                <span className="text-colorText text-sm">
                  {subscription.started_at_formatted ?? '—'}
                </span>
              </article>

              <article>
                <p className="text-muted text-sm">{t('details.period')}</p>
                <span className="text-colorText text-sm">
                  {subscription.current_period_start_formatted ?? '—'}
                  {' — '}
                  {subscription.current_period_end_formatted ??
                    subscription.next_billing_at_formatted ??
                    '—'}
                </span>
              </article>

              {subscription.order_id && (
                <article>
                  <p className="text-muted text-sm">{t('details.signup_order')}</p>
                  <Link
                    to={`/sales/sales-details/${subscription.order_id}`}
                    className="inline-flex items-center gap-1.5 text-interactive text-sm font-medium hover:underline"
                  >
                    {t('details.view_sale_details')}
                    <ExternalLink size={14} />
                  </Link>
                  {(signupOrder?.created_at_formatted || signupOrder?.total_formatted) && (
                    <p className="text-muted text-xs mt-1">
                      {[signupOrder?.created_at_formatted, signupOrder?.total_formatted]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </article>
              )}
            </section>

            {signupOrder?.customer?.phone && (
              <div className="mt-6 gap-4 flex flex-col md:flex-row">
                <Button
                  className="w-full lg:w-[171px]"
                  variant="secondary"
                  onClick={handleWhatsApp}
                >
                  <WhatsIcon />
                  {t('details.send_whatsapp')}
                </Button>
              </div>
            )}
          </section>

          <Divider className="my-8" />
          <SubscriptionBillingSection publicId={subscription.public_id} />

          {currencyConversion && (
            <>
              <Divider className="my-8" />
              <div className="md:space-y-6 space-y-4">
                <div className="flex items-center gap-1">
                  <Conversion />
                  <h2 className="text-colorText font-semibold">
                    {t('details.conversion')}
                  </h2>
                </div>
                <div className="border border-border w-full flex items-start md:items-center gap-2 p-4 rounded-lg">
                  <Info className="text-muted-foreground min-w-5" />
                  <p className="text-muted-foreground text-sm">
                    {currencyConversion.from_currency && currencyConversion.to_currency
                      ? t('sales:paymentReceived', {
                          from: currencyConversion.from_currency,
                          to: currencyConversion.to_currency,
                        })
                      : t('details.conversion_info')}
                  </p>
                </div>
                <section className="grid grid-cols-1 lg:grid-cols-2 text-sm">
                  <article>
                    <p className="text-muted text-sm">{t('sales:paymentValue')}</p>
                    <span className="text-colorText text-sm flex items-center gap-1">
                      {currencyConversion.amount_formatted}
                      <ArrowRight size={12} />
                      {currencyConversion.converted_amount_formatted}
                    </span>
                  </article>
                  <article>
                    <p className="text-muted text-sm">{t('sales:grossValue')}</p>
                    <span className="text-colorText text-sm">
                      {currencyConversion.converted_amount_formatted}
                    </span>
                  </article>
                </section>
              </div>
            </>
          )}

          {saleSliderData && (
            <>
              <Divider className="my-8" />
              <div className="md:space-y-6 space-y-4">
                <div className="flex items-center gap-1">
                  <TagsCircle />
                  <h2 className="text-colorText font-semibold">
                    {t('details.products')}
                  </h2>
                </div>
                <p className="text-muted text-sm">
                  {t('details.products_subscription_hint')}
                </p>
                <section className="flex gap-4 w-full">
                  <SaleSlider sale={saleSliderData} />
                </section>
              </div>
            </>
          )}

          {signupOrder?.customer && (
            <>
              <Divider className="my-8" />
              <div className="md:space-y-6 space-y-4">
                <div className="flex items-center gap-1">
                  <ClientCircle />
                  <h2 className="text-colorText font-semibold">
                    {t('details.client')}
                  </h2>
                </div>
                <section className="grid gap-6 grid-cols-1 lg:grid-cols-2 text-sm">
                  <article>
                    <p className="text-muted text-sm">{t('details.name')}</p>
                    <span className="text-colorText text-sm">
                      {signupOrder.customer.name ?? '—'}
                    </span>
                  </article>
                  <article>
                    <p className="text-muted text-sm">{t('details.email')}</p>
                    <span className="text-colorText text-sm">
                      {signupOrder.customer.email ?? subscription.customer_email ?? '—'}
                    </span>
                  </article>
                  <article>
                    <p className="text-muted text-sm">{t('details.phone')}</p>
                    <span className="text-colorText text-sm">
                      {signupOrder.customer.phone
                        ? `${signupOrder.customer.phone_prefix ?? '55'}${signupOrder.customer.phone}`
                        : '—'}
                    </span>
                  </article>
                  <article>
                    <p className="text-muted text-sm">{t('details.cpf')}</p>
                    <span className="text-colorText text-sm">
                      {signupOrder.customer.document?.document_value ?? '----'}
                    </span>
                  </article>
                  <article>
                    <p className="text-muted text-sm">{t('details.country')}</p>
                    <span className="text-colorText text-sm flex items-center gap-1">
                      {countryLabel}
                    </span>
                  </article>
                </section>
              </div>
            </>
          )}
        </section>
      </div>

      <CancelSubscriptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={handleCancel}
        isLoading={isCanceling}
        endsAtLabel={subscription.next_billing_at_formatted}
      />
    </LayoutInside>
  );
}
